// The one metric-column builder for both tables on this page.
//
// Ad level and asset level had near-identical private copies of this, which is
// how the two drifted apart on tone colours. One copy also means hover
// explanations only have to be wired once.
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Tone, toNum } from './utils';

const TONE_COLOR: Record<Tone, string> = {
  good: '#059669',
  bad: '#DC2626',
  '': 'inherit',
};

export function MetricText({ value, tone = '' }: { value: string; tone?: Tone }) {
  return (
    <Typography
      variant="body2"
      sx={{
        fontVariantNumeric: 'tabular-nums',
        width: '100%',
        textAlign: 'right',
        whiteSpace: 'nowrap',
        color: TONE_COLOR[tone],
        fontWeight: tone ? 500 : 400,
      }}
    >
      {value}
    </Typography>
  );
}

type Formatter = (value: unknown) => string;

export interface MetricColOptions<Row> {
  width?: number;
  tone?: (row: Row) => Tone;
  // What the column means, on hovering the header. The formula lives here.
  help?: string;
  // What this particular row's number is made of, on hovering the cell —
  // original value, operation, result. Return null for rows where there is no
  // arithmetic to show (a missing input), so the cell stays a plain number
  // rather than offering an empty explanation.
  cellHelp?: (row: Row) => string | null;
  // Dates share this builder for their alignment and header treatment, but
  // must not be coerced: an ISO string sorts chronologically on its own,
  // whereas Number('2026-04-25') is NaN and sorts nothing at all.
  sortAs?: 'number' | 'date';
}

// valueGetter feeds sorting (raw number), renderCell feeds display (formatted,
// right-aligned, tabular). Splitting them is what keeps "$1.2k" sortable.
export function buildMetricCol<Row>(
  field: keyof Row & string,
  headerName: string,
  format: Formatter,
  options: MetricColOptions<Row> = {},
): GridColDef {
  const { width = 112, tone, help, cellHelp, sortAs = 'number' } = options;

  const header = (underlined: boolean) => (
    <Box
      component="span"
      sx={{
        fontWeight: 500,
        fontSize: 13,
        lineHeight: 1.2,
        whiteSpace: 'normal',
        ...(underlined
          ? { borderBottom: '1px dotted #94A3B8', cursor: 'help' }
          : {}),
      }}
    >
      {headerName}
    </Box>
  );

  return {
    field,
    headerName,
    width,
    align: 'right',
    headerAlign: 'right',
    type: sortAs === 'date' ? 'string' : 'number',
    sortable: true,
    renderHeader: () =>
      help ? (
        <Tooltip title={help} placement="top">
          {header(true)}
        </Tooltip>
      ) : (
        header(false)
      ),
    // Coerced, because the grid sorts a numeric column by whatever this
    // returns: a measure that arrives as a string would sort lexicographically
    // ("9" above "100") while still displaying correctly.
    valueGetter: params => {
      const raw = (params.row as Row)[field as keyof Row];
      return sortAs === 'date' ? (raw as unknown as string) ?? null : toNum(raw);
    },
    // Identity, deliberately. `type: 'number'` carries MUI's own valueFormatter,
    // which localises — and the CSV export serialises formattedValue, so
    // without this a spend of 5883.42 leaves as the string "5,883.42": a
    // thousands separator inside a comma-delimited file, which then has to be
    // quoted and parsed back out again. Nothing on screen is affected, because
    // renderCell below formats from the raw value and never reads
    // formattedValue.
    valueFormatter: params => params.value,
    renderCell: params => {
      const row = params.row as Row;
      const cell = (
        <MetricText value={format(params.value)} tone={tone ? tone(row) : ''} />
      );
      const math = cellHelp ? cellHelp(row) : null;
      if (!math) return cell;
      return (
        <Tooltip
          title={math}
          placement="top"
          // The math strings put the derivation on a second line. Without
          // pre-line the tooltip collapses the newline and runs them together.
          componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
        >
          {/* Tooltip needs an element that holds a ref and fills the cell, so
              the whole number is hoverable rather than just its glyphs. */}
          <Box sx={{ width: '100%', cursor: 'help' }}>{cell}</Box>
        </Tooltip>
      );
    },
  };
}
